<script>
import { defineAsyncComponent } from "vue";
import { validate } from "vee-validate";

const VueTelInput = defineAsyncComponent(() =>
  import("vue-tel-input").then(async (m) => {
    await import("vue-tel-input/vue-tel-input.css");
    return m.VueTelInput;
  }),
);

export default {
  name: "SnsInput",
  components: { VueTelInput },
  props: {
    modelValue: {
      required: true,
    },
    size: {
      type: String,
      default: "md",
    },
    label: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // todo make this array
    validationRule: {
      type: String,
      default: "",
    },
    inputType: {
      type: String,
      default: "textfield",
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "isValid", "getCountryCode"],
  data() {
    return {
      validatorError: null,
      isPasswordVisible: false,
      hadFocusBeforeDisable: false,
    };
  },
  computed: {
    type() {
      let { type } = this.$attrs;

      if (this.$attrs.type === "password") {
        type = this.isPasswordVisible ? "text" : "password";
      }

      return type;
    },
  },
  watch: {
    disabled(newVal, oldVal) {
      if (newVal && !oldVal) {
        const input = this.$el?.querySelector("input");
        this.hadFocusBeforeDisable = Boolean(input && document.activeElement === input);
      }
      if (!newVal && oldVal && this.hadFocusBeforeDisable) {
        this.hadFocusBeforeDisable = false;
        this.$nextTick(() => {
          const input = this.$el?.querySelector("input");
          if (!input) return;
          const active = document.activeElement;
          if (active && active !== document.body && active !== input) return;
          input.focus();
        });
      }
    },
  },
  // todo refactor validation
  methods: {
    validateField() {
      if (this.validationRule !== "" || this.required) {
        validate(this.modelValue, `${this.required ? "required|" : ""}${this.validationRule}`).then((result) => {
          if (result.valid) {
            this.validatorError = null;
          } else {
            [this.validatorError] = result.errors;
          }

          this.$emit("isValid", result.valid);
        });
      }
    },
    handlePhoneNumber(event) {
      if (event.valid) {
        this.validatorError = null;
      } else if (event.nationalNumber) {
        this.validatorError = this.$t("feedback.validation.phoneNumber");
      }

      this.$emit("isValid", event.valid);
      this.$emit("getCountryCode", event.countryCallingCode);
    },
  },
};
</script>

<template>
  <div id="sns-input">
    <template v-if="inputType === 'textfield'">
      <label class="w-full">
        <div v-if="label !== ''" class="label">
          <div class="label-text">
            {{ label }}
          </div>
        </div>

        <label
          class="flex items-center gap-2 input input-bordered w-full focus:outline-none focus:border-primary"
          :class="{
            'input-sm': size === 'sm',
            'input-md': size === 'md',
            'input-lg': size === 'lg',
          }"
        >
          <div v-if="icon !== ''" class="bg-transparent">
            <i :class="icon" />
          </div>

          <input
            v-bind="$attrs"
            :value="modelValue"
            :type="type"
            :disabled="disabled"
            class="w-full border-0 bg-transparent focus:outline-none"
            @input="$emit('update:modelValue', $event.target.value)"
            @keyup="validateField"
          />

          <button
            v-if="$attrs.type === 'password'"
            type="button"
            class="password-toggle-btn"
            @click="isPasswordVisible = !isPasswordVisible"
          >
            <i :class="`bi-eye${isPasswordVisible ? '-slash' : ''}`" />
          </button>
        </label>

        <div v-if="validatorError" class="label">
          <div class="label-text-alt error-message">
            {{ validatorError }}
          </div>
        </div>
      </label>
    </template>

    <div v-else-if="inputType === 'phone'">
      <vue-tel-input
        v-bind="$attrs"
        :model-value.prop="modelValue"
        class="input input-bordered p-0"
        :class="`input-${size}`"
        :auto-format="false"
        :dropdown-options="{
          showSearchBox: true,
          showFlags: true,
          searchBoxPlaceholder: 'Search by country name or country code',
          showDialCodeInList: true,
          showDialCodeInSelection: true,
        }"
        :disabled="disabled"
        :input-options="{ placeholder: $attrs.placeholder }"
        valid-characters-only
        @input="$emit('update:modelValue', $event.target.value)"
        @validate="handlePhoneNumber"
      />

      <div v-if="validatorError" class="error-message mt-1">
        {{ validatorError }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
#sns-input {
  .input:focus-within {
    outline: none !important;
    @apply border-primary;
  }

  .input:has(> input[disabled]),
  .input:disabled,
  .input-disabled,
  .input[disabled] {
    @apply bg-base-100 opacity-70 border border-accent;
  }

  label {
    @apply text-neutral;
  }

  .password-toggle-btn {
    @apply text-neutral/50 flex-shrink-0;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-size: 1.1rem;
    line-height: 1;

    &:hover {
      @apply text-neutral;
    }
  }

  .error-message {
    @apply text-error;
    font-size: 0.75rem;
    line-height: 1rem;
    animation: shake-vertical 100ms ease-in-out 3;
  }
}

@keyframes shake-vertical {
  0% {
    transform: translate(1px, 0);
  }
  20% {
    transform: translate(-3px, 0);
  }
  30% {
    transform: translate(3px, 0);
  }
  40% {
    transform: translate(1px, 0);
  }
  60% {
    transform: translate(-3px, 0);
  }
  70% {
    transform: translate(3px, 0);
  }
  100% {
    transform: translate(1px, 0);
  }
}
</style>
